<template>
  <div class="mx-auto max-w-7xl space-y-8 p-6">
    <header class="space-y-3 text-center">
      <h1 class="text-3xl font-bold text-theme-fg md:text-4xl">
        Interactive WebRTC QR Demo
      </h1>
      <p class="mx-auto max-w-3xl text-lg text-theme-neutral-6">
        Real peer-to-peer connections with QR codes as the only signalling channel.
        Choose a role to start.
      </p>
    </header>

    <!-- Environment bar: only facts this page can observe -->
    <Card>
      <CardContent class="flex flex-wrap items-center justify-between gap-4 p-4">
        <div class="flex items-center gap-4">
          <!-- navigator.onLine only says a network interface is up; it is not
               a WebRTC connection (it is also true on a hotspot without
               internet, measured in elveris-gecis ice-gathering-sdp). -->
          <StatusIndicator
            :status="isOnline ? 'online' : 'offline'"
            :label="isOnline ? 'Network interface up' : 'Offline'"
          />
          <span v-if="connectionType" class="text-sm text-theme-neutral-6">{{ connectionType }}</span>
        </div>
        <Badge variant="secondary">
          Engine: browser WebRTC
        </Badge>
      </CardContent>
    </Card>

    <Tabs v-model="mode" class="space-y-6">
      <div class="flex justify-center">
        <TabsList>
          <TabsTrigger value="host">
            Host
          </TabsTrigger>
          <TabsTrigger value="client">
            Client
          </TabsTrigger>
        </TabsList>
      </div>

      <!-- Host -->
      <TabsContent value="host" class="space-y-6">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between gap-4">
            <CardTitle>Host connection</CardTitle>
            <StatusIndicator :status="indicator(hostStatus)" :pulse="hostStatus === 'connecting'" :label="statusText(hostStatus, 'host')" />
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="space-y-2">
              <label for="room-name" class="block text-sm font-medium text-theme-fg">Room name (optional)</label>
              <Input
                id="room-name"
                v-model="roomName"
                :disabled="isCreating || invitation !== ''"
                placeholder="Shown to the other side as the invitation label"
                @update:model-value="onRoomNameChange"
              />
              <p class="text-xs text-theme-neutral-6">
                Letters, digits, spaces, "-" and "_"; up to 50 characters.
              </p>
            </div>

            <div class="flex flex-col gap-3 sm:flex-row">
              <Button class="flex-1" :loading="isCreating" :disabled="isCreating || invitation !== ''" @click="createInvitation">
                {{ isCreating ? 'Creating…' : 'Create invitation' }}
              </Button>
              <Button class="flex-1" variant="outline" @click="resetConnection">
                Reset
              </Button>
            </div>

            <div v-if="hostConnectionId" class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-theme-border p-3">
              <div>
                <p class="text-xs text-theme-neutral-6">
                  Connection ID
                </p>
                <p class="font-mono text-sm text-theme-fg">
                  {{ hostConnectionId }}
                </p>
              </div>
              <ClipboardButton :text="hostConnectionId" label="Copy ID" success-message="ID copied" />
            </div>
          </CardContent>
        </Card>

        <div v-if="invitation" class="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>1. Invitation</CardTitle>
              <CardDescription>Show this QR to the client device.</CardDescription>
            </CardHeader>
            <CardContent class="flex flex-col items-center gap-3">
              <QrCode :value="invitation" level="L" :size="288" alt="Invitation QR code" @error="onQrError" />
              <ClipboardButton :text="invitation" label="Copy invitation" success-message="Invitation copied" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Client answer</CardTitle>
              <CardDescription>Scan the answer QR shown on the client device.</CardDescription>
            </CardHeader>
            <CardContent class="space-y-3">
              <template v-if="!answerApplied">
                <QrScanner
                  v-if="hostScanner"
                  :continuous="false"
                  :validate="isAnswer"
                  :show-error="false"
                  :width="288"
                  :height="288"
                  video-label="Camera for scanning the client's answer QR"
                  @detected="(p) => { hostScanner = false; hostAnswerInput = p }"
                  @rejected="error = 'That QR is not an answer. Scan the answer shown on the client device.'"
                  @error="onCameraError"
                />
                <Button v-else variant="outline" size="sm" @click="hostScanner = true">
                  Open camera
                </Button>
                <details>
                  <summary class="cursor-pointer text-sm text-theme-neutral-6">
                    No camera? Paste the answer
                  </summary>
                  <Textarea v-model="hostAnswerInput" :rows="3" class="mt-2 font-mono text-xs" placeholder="{&quot;type&quot;:&quot;answer&quot;, …}" />
                </details>
              </template>
              <p v-else class="text-sm text-theme-ok">
                Answer applied — waiting for the data channel to open.
              </p>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <!-- Client -->
      <TabsContent value="client" class="space-y-6">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between gap-4">
            <CardTitle>Client connection</CardTitle>
            <StatusIndicator :status="indicator(clientStatus)" :pulse="clientStatus === 'connecting'" :label="statusText(clientStatus, 'client')" />
          </CardHeader>
          <CardContent class="space-y-3">
            <template v-if="!answer">
              <p class="text-sm text-theme-fg">
                1. Scan the invitation QR on the host device.
              </p>
              <QrScanner
                v-if="clientScanner"
                :continuous="false"
                :validate="isOffer"
                :show-error="false"
                :width="288"
                :height="288"
                video-label="Camera for scanning the host's invitation QR"
                @detected="(p) => { clientScanner = false; clientInvitationInput = p }"
                @rejected="error = 'That QR is not an invitation. Scan the QR on the host device.'"
                @error="onCameraError"
              />
              <Button v-else variant="outline" size="sm" @click="clientScanner = true">
                Open camera
              </Button>
              <details>
                <summary class="cursor-pointer text-sm text-theme-neutral-6">
                  No camera? Paste the invitation
                </summary>
                <Textarea v-model="clientInvitationInput" :rows="3" class="mt-2 font-mono text-xs" placeholder="{&quot;type&quot;:&quot;webrtc-qr-connection&quot;, …}" />
              </details>
            </template>
            <template v-else>
              <p class="text-sm text-theme-fg">
                2. Show this answer QR to the host device.
              </p>
              <div class="flex flex-col items-center gap-3">
                <QrCode :value="answer" level="L" :size="288" alt="Answer QR code" @error="onQrError" />
                <ClipboardButton :text="answer" label="Copy answer" success-message="Answer copied" />
              </div>
            </template>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>

    <!-- Chat: for whichever role has an open data channel -->
    <Card v-if="activeConnectionId">
      <CardHeader>
        <CardTitle>Data channel open</CardTitle>
        <CardDescription>Role: {{ mode }} · connection {{ activeConnectionId }}</CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <ul class="h-64 space-y-2 overflow-y-auto rounded-lg bg-theme-surface-2 p-4" role="log" aria-live="polite" aria-label="Messages">
          <li
            v-for="(message, index) in chatMessages"
            :key="index"
            class="flex"
            :class="message.sent ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-xs rounded-lg px-4 py-2"
              :class="message.sent ? 'bg-theme-brand text-theme-brand-on' : 'border border-theme-border bg-theme-surface-1 text-theme-fg'"
            >
              <p class="text-sm">
                {{ message.text }}
              </p>
              <p class="mt-1 text-xs opacity-70">
                {{ message.timestamp.toLocaleTimeString() }}
              </p>
            </div>
          </li>
        </ul>
        <form class="flex gap-2" @submit.prevent="sendMessage">
          <Input v-model="newMessage" class="flex-1" placeholder="Type a message…" aria-label="Message" />
          <Button type="submit" :disabled="!newMessage.trim()">
            Send
          </Button>
        </form>
      </CardContent>
    </Card>

    <Alert v-if="error" variant="destructive">
      <AlertDescription>{{ error }}</AlertDescription>
    </Alert>

    <!-- Info -->
    <div class="grid gap-6 md:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle>How to use</CardTitle>
        </CardHeader>
        <CardContent>
          <ol class="list-inside list-decimal space-y-2 text-sm text-theme-fg">
            <li>Device A: Host → "Create invitation".</li>
            <li>Device B: Client → scan A's invitation QR.</li>
            <li>Device A: scan B's answer QR.</li>
            <li>The chat opens when the data channel is open.</li>
          </ol>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
        </CardHeader>
        <CardContent>
          <ul class="list-inside list-disc space-y-2 text-sm text-theme-fg">
            <li>Data channel encrypted with DTLS between the two browsers.</li>
            <li>No signalling server: the offer and answer travel only as QR codes.</li>
            <li>A public STUN server is used to learn your public address; no TURN relay yet.</li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Troubleshooting</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3 text-sm text-theme-fg">
          <p><strong>Camera not working?</strong> It needs HTTPS (or localhost) and camera permission.</p>
          <p><strong>Connection not opening?</strong> Without a TURN relay, networks that block direct connections (some mobile carriers, strict NATs) cannot connect.</p>
          <p><strong>QR not scanning?</strong> Fill most of the camera frame with the QR; the codes are dense.</p>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Technical information</CardTitle>
      </CardHeader>
      <CardContent>
        <dl class="grid gap-x-8 gap-y-2 text-sm md:grid-cols-2">
          <div class="flex gap-2">
            <dt class="w-40 text-theme-neutral-6">
              Browser
            </dt><dd class="text-theme-fg">
              {{ browserInfo }}
            </dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 text-theme-neutral-6">
              WebRTC support
            </dt><dd :class="webrtcSupport ? 'text-theme-ok' : 'text-theme-err'">
              {{ webrtcSupport ? 'Available' : 'Not available' }}
            </dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 text-theme-neutral-6">
              App version
            </dt><dd class="text-theme-fg">
              {{ appVersion }}
            </dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 text-theme-neutral-6">
              QR payload format
            </dt><dd class="font-mono text-theme-fg">
              {{ payloadFormat }}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { QrScannerError } from '@elveris/ui/qr-scanner'
import { Alert, AlertDescription } from '@elveris/ui/alert'
import { Badge } from '@elveris/ui/badge'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'
import { Input } from '@elveris/ui/input'
import { QrCode } from '@elveris/ui/qr-code'
import { QrScanner } from '@elveris/ui/qr-scanner'
import { StatusIndicator } from '@elveris/ui/status-indicator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@elveris/ui/tabs'
import { Textarea } from '@elveris/ui/textarea'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { getProtocolVersion, getVersion } from '@/utils/webrtc-qr'
import { webrtcManager } from '@/utils/webrtc-native'

// Rebuilt on @elveris/ui (2026-09-30). Archived previous version:
// _archive/kit-migration-2026-09-30/WebRTCDemo.vue. Defects fixed on the way:
// - `mode` came from Demo.vue (route /demo/:mode, tutorial "start as …") but
//   this component declared no props or emits, so the route and the tutorial
//   choice were ignored and `connection-established` was never emitted.
// - The chat panel was gated on `connectionInfo`, which only the host set:
//   the client never saw the chat even with an open channel.
// - Messages went to `getActiveConnections()[0]` instead of this role's
//   connection; with both roles in one page they could go the wrong way.
// - Multi-fragment QR inputs ("scan 1 to N", "Add QR code") although the
//   engine always produces exactly one QR; replaced by one camera/paste step
//   that continues automatically (same flow as /chat).
// - "Build:" showed the day the page was opened (`new Date()`), and "Protocol:
//   v{{ protocolVersion }}" rendered "vwebrtc-qr-connection"; "WASM Library"
//   labelled the app version although no WASM is used.

type Role = 'host' | 'client'
type Status = 'idle' | 'connecting' | 'connected' | 'disconnected'

interface ChatMessage {
  text: string
  sent: boolean
  timestamp: Date
}

const props = withDefaults(defineProps<{ mode?: Role }>(), { mode: 'host' })
const emit = defineEmits<{
  'connection-established': [info: { role: Role, connectionId: string }]
}>()

const mode = ref<Role>(props.mode)
watch(() => props.mode, (m) => { mode.value = m })

const hostStatus = ref<Status>('idle')
const clientStatus = ref<Status>('idle')
const roomName = ref('')
const isCreating = ref(false)
const error = ref('')

const hostConnectionId = ref<string | null>(null)
const invitation = ref('')
const hostScanner = ref(false)
const hostAnswerInput = ref('')
const answerApplied = ref(false)

const clientConnectionId = ref<string | null>(null)
const clientScanner = ref(true)
const clientInvitationInput = ref('')
const answer = ref('')

const chatMessages = ref<ChatMessage[]>([])
const newMessage = ref('')

const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const connectionType = ref('')
const browserInfo = ref('')
const webrtcSupport = ref(false)
const appVersion = getVersion()
const payloadFormat = getProtocolVersion()

interface ConnectionPayload {
  type?: unknown
  offer?: unknown
}

// Network Information API (Chromium only, not in the TS DOM lib).
interface NetworkInformationLike extends EventTarget {
  effectiveType?: string
  downlink?: number
}
interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformationLike
}
const networkInfo = (): NetworkInformationLike | undefined =>
  typeof navigator !== 'undefined' ? (navigator as NavigatorWithConnection).connection : undefined

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : '') || String(e)

function parse(text: string): ConnectionPayload | null {
  try {
    return JSON.parse(text.trim())
  }
  catch {
    return null
  }
}
const isAnswer = (text: string) => parse(text)?.type === 'answer'
function isOffer(text: string) {
  const p = parse(text)
  return p?.type === 'webrtc-qr-connection' && Boolean(p.offer)
}

const indicator = (s: Status) => (s === 'connected' ? 'online' : s === 'connecting' ? 'warning' : 'offline')
function statusText(s: Status, role: Role) {
  if (s === 'connected') return 'Connected'
  if (s === 'connecting') return role === 'host' && !answerApplied.value ? 'Waiting for the answer' : 'Connecting…'
  if (s === 'disconnected') return 'Disconnected'
  return 'Ready'
}

const activeConnectionId = computed(() => {
  if (mode.value === 'host' && hostStatus.value === 'connected') return hostConnectionId.value
  if (mode.value === 'client' && clientStatus.value === 'connected') return clientConnectionId.value
  return null
})

function onRoomNameChange() {
  roomName.value = roomName.value.replace(/[^\w\s-]/g, '').slice(0, 50)
}

async function createInvitation() {
  isCreating.value = true
  error.value = ''
  try {
    const { id, qrData } = await webrtcManager.createHostConnection(roomName.value.trim() || 'elveris-demo', 'demo invitation')
    hostConnectionId.value = id
    invitation.value = qrData
    hostStatus.value = 'connecting'
    hostScanner.value = true
  }
  catch (e) {
    error.value = `Could not create the invitation: ${errorMessage(e)}`
  }
  finally {
    isCreating.value = false
  }
}

watch(hostAnswerInput, async (text) => {
  if (!isAnswer(text) || answerApplied.value || !hostConnectionId.value) return
  error.value = ''
  try {
    await webrtcManager.completeConnection(hostConnectionId.value, text.trim())
    // Not connected yet: only the channel's 'open' event means connected.
    answerApplied.value = true
  }
  catch (e) {
    error.value = `The answer could not be applied: ${errorMessage(e)}`
  }
})

watch(clientInvitationInput, async (text) => {
  if (!isOffer(text) || answer.value) return
  error.value = ''
  try {
    const { id, answerData } = await webrtcManager.acceptConnection(text.trim())
    clientConnectionId.value = id
    answer.value = answerData
    clientStatus.value = 'connecting'
  }
  catch (e) {
    error.value = `The invitation could not be accepted: ${errorMessage(e)}`
  }
})

function onCameraError(e: QrScannerError) {
  error.value = `${e.message} You can paste the text instead.`
}

function onQrError(e: Error) {
  error.value = `QR could not be generated (${e.message}). Use the copy button instead.`
}

function sendMessage() {
  const text = newMessage.value.trim()
  const id = activeConnectionId.value
  if (!text || !id) return
  if (!webrtcManager.sendMessage(id, text)) {
    error.value = 'Not sent: the data channel is not open.'
    return
  }
  chatMessages.value.push({ text, sent: true, timestamp: new Date() })
  newMessage.value = ''
}

function onMessage(event: Event) {
  const { connectionId, message } = (event as CustomEvent).detail ?? {}
  if (connectionId && connectionId === activeConnectionId.value) {
    chatMessages.value.push({ text: String(message), sent: false, timestamp: new Date() })
  }
}

function onState(event: Event) {
  const { connectionId, state } = (event as CustomEvent).detail ?? {}
  const role: Role | null = connectionId === hostConnectionId.value ? 'host' : connectionId === clientConnectionId.value ? 'client' : null
  if (!role) return
  const status = role === 'host' ? hostStatus : clientStatus
  if (state === 'open') {
    status.value = 'connected'
    emit('connection-established', { role, connectionId })
  }
  else if (state === 'closed' || state === 'error') {
    status.value = 'disconnected'
    if (state === 'error') error.value = 'The data channel reported an error.'
  }
}

function resetConnection() {
  if (hostConnectionId.value) webrtcManager.closeConnection(hostConnectionId.value)
  if (clientConnectionId.value) webrtcManager.closeConnection(clientConnectionId.value)
  hostConnectionId.value = clientConnectionId.value = null
  hostStatus.value = clientStatus.value = 'idle'
  invitation.value = answer.value = ''
  hostAnswerInput.value = clientInvitationInput.value = ''
  answerApplied.value = false
  hostScanner.value = false
  clientScanner.value = true
  chatMessages.value = []
  error.value = ''
}

function updateConnectionType() {
  const c = networkInfo()
  connectionType.value = c?.effectiveType ? `${c.effectiveType}${c.downlink ? ` (${c.downlink} Mbps)` : ''}` : ''
}
const setOnline = () => { isOnline.value = true }
const setOffline = () => { isOnline.value = false }

onMounted(() => {
  const ua = navigator.userAgent
  const browser = ua.includes('Edg/') ? 'Edge' : ua.includes('Chrome') ? 'Chrome' : ua.includes('Firefox') ? 'Firefox' : ua.includes('Safari') ? 'Safari' : 'Unknown'
  browserInfo.value = `${browser} · ${navigator.platform}`
  webrtcSupport.value = Boolean(window.RTCPeerConnection && window.RTCDataChannel)
  updateConnectionType()
  networkInfo()?.addEventListener?.('change', updateConnectionType)
  window.addEventListener('online', setOnline)
  window.addEventListener('offline', setOffline)
  window.addEventListener('webrtc-message', onMessage)
  window.addEventListener('webrtc-state', onState)
})

onUnmounted(() => {
  networkInfo()?.removeEventListener?.('change', updateConnectionType)
  window.removeEventListener('online', setOnline)
  window.removeEventListener('offline', setOffline)
  window.removeEventListener('webrtc-message', onMessage)
  window.removeEventListener('webrtc-state', onState)
  if (hostConnectionId.value) webrtcManager.closeConnection(hostConnectionId.value)
  if (clientConnectionId.value) webrtcManager.closeConnection(clientConnectionId.value)
})
</script>
